import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { catchError, Observable, tap } from 'rxjs';
import { buildPaymentRequest, mapPatientBalance, PatientBalance } from '../../models';
import { ApiService, SentryService } from '../../services';
import { PatientState } from '../patient/patient.state';
import { ApiStoreData, defaultApiStoreData, MissingApiRequestDataError } from '../utils';
import { FetchPatientBalance, MakePayment } from './payments.actions';

export interface PaymentsStateModel {
  patientBalance: ApiStoreData<PatientBalance | null>;
  makePayment: ApiStoreData<null>;
}

@State<PaymentsStateModel>({
  name: 'payments',
  defaults: {
    patientBalance: defaultApiStoreData,
    makePayment: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class PaymentsState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static makePaymentState(state: PaymentsStateModel): ApiStoreData<null> {
    return state.makePayment;
  }

  @Selector()
  static patientBalanceState(state: PaymentsStateModel): ApiStoreData<PatientBalance | null> {
    return state.patientBalance;
  }

  @Action(MakePayment)
  makePayment(ctx: StateContext<PaymentsStateModel>, action: MakePayment) {
    const body = buildPaymentRequest(action.formValues);

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'makePayment' });
    }

    ctx.setState(
      produce((draft) => {
        draft.makePayment.isLoading = true;
      })
    );

    return this.apiService.makePayment(body, patientId).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'makePayment' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'makePayment', resetData: false })
      )
    );
  }

  @Action(FetchPatientBalance)
  fetchPatientBalance(ctx: StateContext<PaymentsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.patientBalance.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'patientBalance' });
    }

    return this.apiService.getPatientBalance(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPatientBalance(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'patientBalance' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'patientBalance' }))
    );
  }
}
